import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getAvailableTimeSlots, checkVideoCallPackage } from '../../utils/videoCallUtils';
import { createVideoCallRequest } from '../../utils/videoCallDatabase';
import VideoCallLimitModal from './VideoCallLimitModal';
import VideoCallUsageBadge from './VideoCallUsageBadge';
import { useTranslation } from 'react-i18next';

const RequestVideoCallModal = ({ isOpen, onClose, product, store }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [packageInfo, setPackageInfo] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  useEffect(() => {
    if (isOpen && store?.id) {
      loadAvailableSlots();
      checkPackage();
    }
  }, [isOpen, selectedDate, store]);

  const loadAvailableSlots = async () => {
    if (!store?.id) return;
    
    try {
      setLoadingSlots(true);
      const slots = await getAvailableTimeSlots(store.id, product?.id, selectedDate);
      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error loading available slots:', error);
    } finally {
      setLoadingSlots(false);
    }
  };

  const checkPackage = async () => {
    if (!currentUser) return;
    
    try {
      const info = await checkVideoCallPackage(currentUser.uid);
      setPackageInfo(info);
    } catch (error) {
      console.error('Error checking package:', error);
    }
  };

  const handleRequest = async () => {
    if (!selectedTimeSlot || !currentUser || !store || !product) {
      alert(t('videoCall.selectTime'));
      return;
    }

    // Check package
    if (!packageInfo?.hasPackage) {
      alert(t('videoCall.packageRequired'));
      return;
    }

    if (packageInfo.callsRemaining === 0) {
      setShowLimitModal(true);
      return;
    }

    try {
      setLoading(true);
      
      // Create scheduled time
      const [hours, minutes] = selectedTimeSlot.start.split(':');
      const scheduledDateTime = new Date(selectedDate);
      scheduledDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      // Create request
      const requestData = {
        storeId: store.id,
        productId: product.id,
        buyerId: currentUser.uid,
        sellerId: store.ownerId,
        requestedTime: new Date(),
        scheduledTime: scheduledDateTime,
        duration: 20, // minutes
        productName: product.name,
        buyerName: currentUser.displayName || currentUser.email,
        storeName: store.name || 'N/A' // Add store name for buyer view
      };

      console.log('📤 Creating video call request:', {
        storeId: requestData.storeId,
        buyerId: requestData.buyerId,
        productName: requestData.productName,
        scheduledTime: requestData.scheduledTime
      });

      const result = await createVideoCallRequest(requestData);
      console.log('✅ Video call request created:', result);
      
      setRequestSent(true);
      // Auto close after 3 seconds
      setTimeout(() => {
        onClose();
        setRequestSent(false);
      }, 3000);
    } catch (error) {
      console.error('Error creating video call request:', error);
      alert(t('common.sendRequestError') + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getNext7Days = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">{t('videoCall.requestTitle')}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Product Info */}
          {product && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">{t('common.product')}</p>
              <p className="font-semibold text-gray-800">{product.name}</p>
              {product.price && (
                <p className="text-sm text-gray-600 mt-1">฿{product.price.toLocaleString()}</p>
              )}
            </div>
          )}

          {/* Package Info with Progress Bar */}
          {packageInfo && (
            <div>
              <VideoCallUsageBadge userId={currentUser?.uid} showFullInfo={true} />
            </div>
          )}

          {/* Date Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">{t('videoCall.selectDate')}</label>
            <div className="grid grid-cols-7 gap-2">
              {getNext7Days().map((date, index) => {
                const dateStr = date.toISOString().split('T')[0];
                const isSelected = dateStr === selectedDate.toISOString().split('T')[0];
                const isToday = index === 0;
                
                return (
                  <button
                    key={dateStr}
                    onClick={() => setSelectedDate(date)}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      isSelected
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-xs text-gray-600">
                      {isToday ? t('videoCall.today') : date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className="text-lg font-semibold mt-1">
                      {date.getDate()}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time Slots */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">{t('videoCall.selectTime')}</label>
            {loadingSlots ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
              </div>
            ) : availableSlots.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">⏰</div>
                <p className="text-gray-700 font-semibold mb-2">{t('videoCall.noSlotsToday')}</p>
                <p className="text-sm text-gray-500 mb-4">
                  {t('videoCall.noScheduleSet')}
                </p>
                <p className="text-xs text-gray-400">
                  {t('videoCall.pleaseNotifyStore')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {availableSlots.map((slot, index) => {
                  const isSelected = selectedTimeSlot?.start === slot.start && selectedTimeSlot?.end === slot.end;
                  return (
                    <button
                      key={index}
                      onClick={() => setSelectedTimeSlot(slot)}
                      disabled={!slot.available}
                      className={`p-4 rounded-lg border-2 transition-colors ${
                        isSelected
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : slot.available
                          ? 'border-gray-200 hover:border-gray-300'
                          : 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <div className="font-semibold">{slot.start} - {slot.end}</div>
                      {slot.available ? (
                        <div className="text-xs text-gray-600 mt-1">
                          {slot.slotsRemaining} {slot.slotsRemaining > 1 ? t('videoCall.slots') : t('videoCall.slot')} {t('common.available')}
                        </div>
                      ) : (
                        <div className="text-xs text-red-500 mt-1">{t('videoCall.fullSlot')}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Note */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              {t('videoCall.storeWillNotify')}
            </p>
            <p className="text-xs text-blue-700 mt-1">
              {t('videoCall.youWillBeNotified')}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex items-center justify-end gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleRequest}
            disabled={!selectedTimeSlot || loading || !packageInfo?.hasPackage || packageInfo?.callsRemaining === 0}
            className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('videoCall.sending') : t('videoCall.requestVideoCall')}
          </button>
        </div>
      </div>

      {/* Request Confirmation */}
      {requestSent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md text-center">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('videoCall.requestSent')}</h2>
            <p className="text-gray-600 mb-4">
              {t('videoCall.requestSentMessage')}
            </p>
            <p className="text-sm text-gray-500">
              {t('videoCall.storeWillRespond')}
            </p>
          </div>
        </div>
      )}

      {/* Limit Modal */}
      <VideoCallLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        packageInfo={packageInfo}
      />
    </div>
  );
};

export default RequestVideoCallModal;

